import { useMemo } from 'react';
import { FilterOption, FilterAggregations } from '../types';

export interface ChatDataFilterOptions {
  categories: FilterOption[];
  domains: FilterOption[];
  subDomains: FilterOption[];
  documents: FilterOption[];
}

/**
 * Transforms server-provided aggregations into filter options for the chat filter UI.
 * Documents are handled separately via useFilterDocuments hook with server-side pagination.
 */
export const useDataDrivenFilterOptions = (aggregations?: FilterAggregations): ChatDataFilterOptions => {
  return useMemo(() => {
    const empty: ChatDataFilterOptions = { categories: [], domains: [], subDomains: [], documents: [] };

    if (!aggregations) return empty;

    const createOptionsFromAggregations = (counts: Record<string, number>): FilterOption[] => {
      return Object.entries(counts)
        .map(([name, count]) => ({ label: name, value: name, count, disabled: count === 0 }))
        .sort((a, b) => {
          if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
          return a.label.localeCompare(b.label);
        });
    };

    return {
      categories: createOptionsFromAggregations(aggregations.category_counts),
      domains: createOptionsFromAggregations(aggregations.domain_counts),
      subDomains: createOptionsFromAggregations(aggregations.subdomain_counts),
      documents: [] // Documents are fetched via useFilterDocuments with server-side pagination
    };
  }, [aggregations]);
};
